import { BadStatusError, cfetch } from '@confluence/network';

// Only the slice of the jira-metadata response this reader walks.
type JiraMetadataResponse = {
	groups?: ReadonlyArray<{
		type?: string | null;
		items?: ReadonlyArray<{ url?: string | null }> | null;
	}> | null;
};

// The endpoint also groups sprints and versions, which are not work items.
const WORK_ITEM_GROUP_TYPES = new Set(['ISSUES', 'EPICS']);

export type LinkedJiraWorkItemsErrorType = 'timeout' | 'badStatus' | 'parse' | 'network';

const getEndpoint = (contentId: string): string =>
	`/wiki/rest/jira-metadata/1.0/metadata?pageId=${encodeURIComponent(contentId)}`;

/**
 * URLs of the Jira work items holding a remote link to this content, from the REST endpoint that
 * backs the page's "Jira links" byline. Stand-in for the graph while it does not reliably ingest
 * the work item -> page attachments AI Planner creates. CCINT-9346
 */
export const fetchLinkedJiraWorkItemUrls = async (
	contentId: string,
	signal?: AbortSignal,
): Promise<string[]> => {
	const response = await cfetch(getEndpoint(contentId), {
		method: 'GET',
		headers: { Accept: 'application/json' },
		credentials: 'include',
		signal,
	});
	const body: JiraMetadataResponse = await response.json();

	const urls = new Set<string>();
	(body.groups ?? []).forEach((group) => {
		if (!WORK_ITEM_GROUP_TYPES.has(group.type ?? '')) {
			return;
		}
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- linear in total item count; four fixed group types partition it
		(group.items ?? []).forEach((item) => {
			if (item.url) {
				urls.add(item.url);
			}
		});
	});
	return [...urls];
};

/** 4xx: anonymous viewer or no Jira app link. Expected, so not worth a monitoring report. */
export const isLinkedJiraWorkItemsClientError = (error: unknown): boolean =>
	BadStatusError.isBadStatusError(error) && error.response.status < 500;

export const classifyLinkedJiraWorkItemsError = (
	error: unknown,
	timedOut: boolean,
): LinkedJiraWorkItemsErrorType => {
	if (timedOut) {
		return 'timeout';
	}
	if (BadStatusError.isBadStatusError(error)) {
		return 'badStatus';
	}
	if (error instanceof SyntaxError) {
		return 'parse';
	}
	return 'network';
};
